import { Layout } from "@/components/Layout";

function LegalShell({ title, eyebrow, updated, children }) {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-16">
        <div className="section-label mb-3">— {eyebrow}</div>
        <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight mb-3">{title}</h1>
        <div className="font-mono text-xs text-[#52525B] mb-10">Last updated: {updated}</div>
        <div className="space-y-6 font-plex text-[#9CA3AF] leading-relaxed">{children}</div>
      </div>
    </Layout>
  );
}

function H2({ children }) {
  return <h2 className="font-chivo font-bold text-xl text-white uppercase tracking-tight pt-4">{children}</h2>;
}

export function Terms() {
  return (
    <LegalShell title="Terms of Service" eyebrow="LEGAL" updated="February 2026">
      <p>
        These Terms of Service ("Terms") govern your access to and use of the Dry Ice Connect member portal
        ("Service") operated by Dry Ice Bulls ("we", "us", "our"). By creating an account or activating a
        membership, you agree to be bound by these Terms.
      </p>

      <H2>1. Eligibility & Accounts</H2>
      <p>You must be at least 18 years old and authorized to enter into contracts to use the Service. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.</p>

      <H2>2. Membership & Billing</H2>
      <p>Dry Ice Connect is offered as an annual subscription billed at $399 USD per year. Payments are processed through Stripe. By activating your membership, you authorize us to charge your selected payment method on each renewal date unless you have cancelled auto-renewal.</p>

      <H2>3. Cancellation</H2>
      <p>You may cancel auto-renewal at any time from your account page. Your membership remains active until the end of the paid term; you will continue to have full access through that date. No partial refunds are provided for cancellation mid-term except as described in our <a href="/refund-policy" className="text-[#00D4FF] hover:underline">Refund Policy</a>.</p>

      <H2>4. Member Conduct & Restrictions</H2>
      <p>Members shall not (a) attempt to communicate with or identify other members through the Service; (b) reproduce, redistribute, resell, or publicly share videos, downloads, documents, or pricing obtained through the Service; (c) attempt to access tickets, accounts, or content belonging to other members; (d) reverse engineer or interfere with the operation of the Service.</p>

      <H2>5. Intellectual Property</H2>
      <p>All content within the Service — including training videos, documents, templates, pricing information, and software — is the property of Dry Ice Bulls or its licensors and is protected by copyright and other applicable laws. Members receive a limited, non-transferable license to use the content for their own business operations only.</p>

      <H2>6. Member Pricing & Inventory</H2>
      <p>Member pricing is provided as a benefit of active membership and is subject to change. Early-access inventory is offered to active members on a first-come, first-served basis for a 5-day window prior to public release. Listed prices do not include taxes, shipping, or installation unless explicitly stated.</p>

      <H2>7. Support & Communication</H2>
      <p>Support tickets are private between the member and Dry Ice Bulls administrators. We aim to respond to tickets within two business days but make no guaranteed response time.</p>

      <H2>8. Termination</H2>
      <p>We may suspend or terminate your account if you violate these Terms, abuse the Service, or attempt to circumvent restrictions. In cases of termination for cause, no refund will be issued.</p>

      <H2>9. Disclaimers & Limitation of Liability</H2>
      <p>The Service and all content are provided "as is" without warranties of any kind. Dry Ice blasting is an industrial process with inherent risks; all training, guidance, and documentation provided through the Service are for informational purposes only and do not replace proper safety training, equipment certifications, or applicable regulations. To the maximum extent permitted by law, our total liability for any claim arising from the Service shall not exceed the amount paid by you in the preceding twelve months.</p>

      <H2>10. Changes to These Terms</H2>
      <p>We may update these Terms from time to time. Material changes will be posted in the member portal and, where appropriate, communicated via email. Continued use after changes constitutes acceptance.</p>

      <H2>11. Governing Law</H2>
      <p>These Terms are governed by the laws of the United States and the state in which Dry Ice Bulls is incorporated, without regard to conflict-of-laws principles.</p>

      <H2>12. Contact</H2>
      <p>Questions about these Terms: <a href="mailto:support@dryiceconnect.com" className="text-[#00D4FF] hover:underline">support@dryiceconnect.com</a></p>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy" eyebrow="LEGAL" updated="February 2026">
      <p>
        This Privacy Policy explains how Dry Ice Bulls ("we", "us") collects, uses, and protects information
        you provide through the Dry Ice Connect member portal.
      </p>

      <H2>1. Information We Collect</H2>
      <p>
        <strong className="text-white">Account information:</strong> name, email, company, phone, and password (stored as a salted hash).<br />
        <strong className="text-white">Billing information:</strong> handled and stored by Stripe; we receive only transaction metadata (session ID, amount, payment status, last 4 of card).<br />
        <strong className="text-white">Support data:</strong> ticket subjects, messages, and any files you upload.<br />
        <strong className="text-white">Usage data:</strong> standard server logs (IP, timestamps, referring URLs) used for security and abuse prevention.
      </p>

      <H2>2. How We Use Information</H2>
      <p>We use your information to (a) provide and operate the Service; (b) process payments and manage your membership; (c) respond to support requests; (d) send transactional emails such as renewal reminders, payment confirmations, and password reset links; (e) detect and prevent abuse.</p>

      <H2>3. Email Communication</H2>
      <p>Transactional emails are delivered via Resend. We do not send marketing emails without your consent. You may not opt out of transactional emails essential to your account (e.g. billing receipts) while you maintain an active membership.</p>

      <H2>4. Sharing of Information</H2>
      <p>We do not sell your personal information. We share data only with the service providers required to operate the Service: Stripe (payments), Resend (email delivery), and our cloud hosting and storage providers. Each operates under their own privacy policies and contractual data protections.</p>

      <H2>5. Member Privacy</H2>
      <p>Members cannot see other members. Names, emails, support tickets, and account details are visible only to you and authorized Dry Ice Bulls administrators.</p>

      <H2>6. Security</H2>
      <p>Passwords are hashed using bcrypt. Authentication tokens are stored as HTTP-only cookies. Files are stored in encrypted object storage. While we apply industry-standard protections, no system is perfectly secure; report suspected vulnerabilities to <a href="mailto:support@dryiceconnect.com" className="text-[#00D4FF] hover:underline">support@dryiceconnect.com</a>.</p>

      <H2>7. Data Retention</H2>
      <p>We retain account and billing records for as long as your membership is active and for a reasonable period afterward to satisfy legal, accounting, and reporting requirements. You may request deletion of your account and associated data by contacting support, subject to any retention obligations.</p>

      <H2>8. Your Rights</H2>
      <p>Depending on your jurisdiction, you may have rights to access, correct, port, or delete your personal information. Submit requests to <a href="mailto:support@dryiceconnect.com" className="text-[#00D4FF] hover:underline">support@dryiceconnect.com</a> and we will respond within 30 days.</p>

      <H2>9. Cookies</H2>
      <p>We use essential cookies for authentication and session management. We do not use third-party analytics or advertising cookies in the member portal.</p>

      <H2>10. Changes</H2>
      <p>We may update this Policy from time to time. Material changes will be posted in the portal.</p>

      <H2>11. Contact</H2>
      <p>Privacy questions: <a href="mailto:support@dryiceconnect.com" className="text-[#00D4FF] hover:underline">support@dryiceconnect.com</a></p>
    </LegalShell>
  );
}

export function Refund() {
  return (
    <LegalShell title="Refund Policy" eyebrow="LEGAL" updated="February 2026">
      <p>
        This Refund Policy applies to the $399 annual Dry Ice Connect membership. It does not apply to
        equipment, parts, or other products purchased through Dry Ice Bulls; those are governed by their
        respective sales agreements.
      </p>

      <H2>1. 7-Day Refund Window</H2>
      <p>If you are not satisfied with your membership, you may request a full refund within <strong className="text-white">seven (7) days</strong> of your initial payment, provided you have not downloaded or substantially consumed paid content (training videos, downloads, or member pricing usage).</p>

      <H2>2. How to Request a Refund</H2>
      <p>Submit a support ticket from your member account or email <a href="mailto:support@dryiceconnect.com" className="text-[#00D4FF] hover:underline">support@dryiceconnect.com</a> with the subject "Refund Request" and your account email. Refunds are processed back to the original payment method within 5–10 business days of approval.</p>

      <H2>3. Cancellation vs. Refund</H2>
      <p>Cancelling auto-renewal stops future charges but does not refund the current term. Your membership remains active until expiration. To stop a renewal, use the "Cancel Auto-Renewal" button on your account page at any time before the renewal date.</p>

      <H2>4. After the 7-Day Window</H2>
      <p>Refunds are generally not provided after seven days. Exceptions may be made at our sole discretion in cases of duplicate charges, billing errors, or extended platform outages.</p>

      <H2>5. Renewals</H2>
      <p>Annual renewals are non-refundable once charged unless you contact us within 7 days of the renewal charge and have not actively used member benefits during that period.</p>

      <H2>6. Chargebacks</H2>
      <p>Initiating a chargeback without first contacting support may result in immediate account suspension. Please reach out — we resolve nearly all billing concerns quickly and directly.</p>

      <H2>7. Contact</H2>
      <p>All refund-related questions: <a href="mailto:support@dryiceconnect.com" className="text-[#00D4FF] hover:underline">support@dryiceconnect.com</a></p>
    </LegalShell>
  );
}
